import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { ArrowLeft } from "lucide-react";
import { BLOG_POSTS } from "@/lib/content";

export default function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);
    if (!post) return <Navigate to="/blog" replace />;

    return (
        <div className="page-fade pt-32 md:pt-40 pb-24">
            <Seo
                title={`${post.title} — Aqualyx NZ`}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                type="article"
                image={post.image}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Journal", path: "/blog" },
                    { name: post.title, path: `/blog/${post.slug}` },
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": post.title,
                    "description": post.excerpt,
                    "image": [post.image],
                    "datePublished": post.date,
                    "dateModified": post.date,
                    "author": { "@type": "Organization", "name": "Aqualyx New Zealand", "url": "https://aqualyxnz.co.nz/" },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Aqualyx New Zealand",
                        "logo": { "@type": "ImageObject", "url": "https://dermalis.co.nz/aqualyx-hero.png" }
                    },
                    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://aqualyxnz.co.nz/blog/${post.slug}` },
                    "articleSection": post.category,
                    "inLanguage": "en-NZ"
                }}
            />
            <Helmet>
                <meta property="article:published_time" content={post.date} />
                <meta property="article:section" content={post.category} />
                <meta name="keywords" content={`${post.title}, Aqualyx NZ, fat dissolving injections, ${post.category} New Zealand`} />
            </Helmet>
            <article className="max-w-3xl mx-auto px-6 md:px-10">
                <Link to="/blog" className="inline-flex items-center gap-2 eyebrow text-ink/60 hover:text-champagne mb-8">
                    <ArrowLeft size={14} /> Back to Journal
                </Link>
                <div className="eyebrow mb-4">{post.category} · {post.read}</div>
                <h1 className="editorial-headline text-5xl md:text-7xl mb-8">{post.title}</h1>
                <p className="font-serif italic text-2xl text-ink/70 mb-10 leading-snug">{post.excerpt}</p>
                <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover mb-10" />
                <div className="space-y-6 text-ink/80 leading-relaxed">
                    {post.body.map((p, i) => (
                        <p key={i} className="text-lg">{p}</p>
                    ))}
                </div>
                <div className="mt-16 p-8 bg-nude border border-champagne/40">
                    <div className="eyebrow text-champagne mb-3">Medical Disclaimer</div>
                    <p className="text-sm text-ink/70 leading-relaxed">
                        Aqualyx™ is a Class III medical device. Aqualyx™ has risks and benefits. Consult your healthcare professional to determine if Aqualyx™ is right for you. Medsafe registered in New Zealand. Individual results may vary.
                    </p>
                </div>
                <div className="mt-10 flex gap-3 flex-wrap">
                    <Link to="/clinics" className="btn-primary">Find Certified Clinic</Link>
                    <Link to="/contact" className="btn-outline">Book Consultation</Link>
                </div>
            </article>
        </div>
    );
}
